import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { serverEncryption } from "@/shared/utils/serverEncryption";

interface SaveEINParams {
  ein: string;
  estateId: number;
}

interface UseSaveEINResult {
  saveEIN: (params: SaveEINParams) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function useSaveEIN(): UseSaveEINResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ ein, estateId }: SaveEINParams) => {
      if (!estateId) {
        throw new Error("Estate ID is required");
      }

      const encrypted = await serverEncryption.encrypt(ein, "person", estateId, "ein");

      const { error } = await supabase
        .from("estates")
        .update({ ein_encrypted: encrypted })
        .eq("estate_id", estateId);

      if (error) {
        throw new Error(`Failed to save encrypted EIN: ${error.message}`);
      }
    },
    onSuccess: (_result, { estateId }) => {
      queryClient.invalidateQueries({ queryKey: ["personData", estateId] });
      queryClient.invalidateQueries({ queryKey: ["estates"] });
      queryClient.invalidateQueries({ queryKey: ["deceasedEINData", estateId] });
    },
  });

  return {
    saveEIN: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error?.message || null,
  };
}
